import React from 'react';
import { Link } from 'react-router-dom';
import { useLang } from '../../i18n/LangContext';
import { ArrowRight } from 'lucide-react';
import BeforeAfter from '../BeforeAfter';
import { setEstimateService } from '../../utils/estimatePreselect';

const YARD_BEFORE = 'https://static.prod-images.emergentagent.com/jobs/e72d340d-3899-4876-891e-4e00170d4616/images/17038ef5559850b285a4db70bc42d12f6272f0b2d5fada9e29de54bea3431aed.jpeg';
const YARD_AFTER = 'https://static.prod-images.emergentagent.com/jobs/e72d340d-3899-4876-891e-4e00170d4616/images/b3c688c1a6ef759cfda84e90ae8232b3de81ab1ae06a0dbf963db8fe7875ba34.jpeg';

export default function OutdoorLiving() {
  const { t } = useLang();
  const c = t.compare;
  const scrollTo = (id) => document.querySelector(id)?.scrollIntoView({ behavior: 'smooth' });
  const explore = () => {
    setEstimateService({ category: 'Outdoor Living & Entertainment', detail: 'Outdoor Living', page: '/#outdoor-living' });
    window.dispatchEvent(new Event('mmt:estimate-preselect'));
    scrollTo('#estimate');
  };

  return (
    <section id="outdoor-living" className="relative bg-[#12151A] py-20 sm:py-28 overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <span className="text-[#D4AF37] text-xs font-mono tracking-[0.25em] uppercase">{t.outdoor.label}</span>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mt-3 mb-6 font-outfit">{t.outdoor.heading}</h2>
            <p className="text-slate-300 leading-relaxed mb-6">{t.outdoor.desc}</p>
            <div className="grid grid-cols-2 gap-3 mb-8">
              {t.outdoor.items.map(item => (<div key={item} className="flex items-center gap-2 text-slate-300 text-sm"><div className="w-1.5 h-1.5 rounded-full bg-[#D4AF37]" />{item}</div>))}
            </div>
            <div className="flex flex-wrap gap-3">
              <button onClick={() => scrollTo('#estimate')} data-testid="outdoor-cta"
                className="group inline-flex items-center gap-3 bg-[#D4AF37] hover:bg-[#E5C158] text-black font-bold px-8 py-4 rounded transition-all">
                {t.outdoor.cta}<ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </button>
              <Link to="/outdoor-living-miami" data-testid="outdoor-learn-more"
                className="inline-flex items-center gap-2 border border-white/15 hover:border-[#D4AF37]/40 text-white font-semibold px-6 py-4 rounded transition-all">
                {t.services.explore}<ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-lg overflow-hidden aspect-[3/4]"><img src="https://images.unsplash.com/photo-1762117360868-d4e757073d45?w=600" alt="Outdoor kitchen inspiration" width="600" height="800" className="w-full h-full object-cover" loading="lazy" /></div>
            <div className="rounded-lg overflow-hidden aspect-[3/4] mt-8"><img src="https://images.unsplash.com/photo-1610882290669-102aadebb413?w=600" alt="Outdoor entertainment space" width="600" height="800" className="w-full h-full object-cover" loading="lazy" /></div>
          </div>
        </div>

        {/* AI before/after concept (clearly labeled, not a completed project) */}
        <div className="mt-16 max-w-4xl mx-auto">
          <h3 className="text-xl sm:text-2xl font-bold text-white font-outfit mb-2 text-center">{c.backyardHeading}</h3>
          <p className="text-slate-400 text-sm mb-6 text-center">{c.sectionNote}</p>
          <BeforeAfter
            beforeSrc={YARD_BEFORE}
            afterSrc={YARD_AFTER}
            beforeAlt={`${c.existingIllustrative} — Miami backyard`}
            afterAlt={`${c.concept} — Miami outdoor living`}
            beforeLabel={c.existingIllustrative}
            afterLabel={c.concept}
            onExplore={explore}
          />
        </div>
      </div>
    </section>
  );
}
